/**
 * Login Page Component
 * @implements TS-023 - Login UI
 * @implements FS-001.4.1 - Login Page Component
 * @implements FS-001.2.1 - GitHub OAuth Authentication
 * @implements FS-001.2.2 - Password Authentication (Backup), OAuth Failure Detection
 * @implements FS-001.5.2 - Return URL handling after login
 * @implements US-008b - Password Setup UI (OAuth error fallback)

 *
 * Unified login page providing:
 * - GitHub OAuth as primary authentication method
 * - Password login form (toggle to show/hide)
 * - OAuth error handling from URL query params
 * - Auto-show password form after OAuth provider errors
 * - Loading states during authentication
 * - Return URL redirect after successful login (for session expiration flow)
 * - Link to registration for new users
 */

import { useState, useEffect, FormEvent } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Divider,
  TextField,
  Alert,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  Link as RouterLink,
  useSearchParams,
  useNavigate,
} from 'react-router-dom';

import { GitHubOAuthButton, GoogleOAuthButton } from '@/components/auth';
import { useAuthContext } from '@/providers/AuthProvider';

/**
 * OAuth error codes that indicate provider unavailability
 * These errors should trigger the password fallback UI
 * @implements FS-001.2.2 - OAuth Failure Detection
 */
const OAUTH_PROVIDER_ERROR_CODES = [
  'oauth_failed',
  'provider_unavailable',
  'oauth_timeout',
];

/**
 * Format a provider name for display
 */
const formatProviderName = (provider: string): string => {
  const names: Record<string, string> = {
    github: 'GitHub',
    google: 'Google',
  };
  return names[provider.toLowerCase()] || provider;
};

/**
 * Map OAuth error codes to user-friendly messages
 * @implements FS-001.4.1 - Error handling

 */
const getErrorMessage = (code: string, provider?: string | null): string => {
  if (code === 'provider_mismatch' && provider) {
    const providerName = formatProviderName(provider);
    return `This email is already registered with ${providerName}. Please sign in using ${providerName} instead.`;
  }

  const messages: Record<string, string> = {
    provider_mismatch: 'Please sign in with your registered OAuth provider.',
    user_not_found: 'No account found. Please register first.',
    account_suspended: 'Your account has been suspended. Contact support.',
    oauth_cancelled: 'Authentication was cancelled.',
    oauth_failed:
      'OAuth authentication failed. Try using your password if you have one set.',
    provider_unavailable:
      'OAuth provider is currently unavailable. Try using your password.',
    oauth_timeout:
      'OAuth request timed out. Try using your password if available.',
  };
  return messages[code] || 'An error occurred. Please try again.';
};

/**
 * Check if error indicates OAuth provider failure
 * @implements FS-001.2.2 - OAuth Failure Detection
 */
const isOAuthProviderError = (code: string | null): boolean => {
  return code !== null && OAUTH_PROVIDER_ERROR_CODES.includes(code);
};

export const LoginPage = () => {
  // Form state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Routing
  const navigate = useNavigate();

  // URL params for OAuth callback errors and return URL
  const [searchParams] = useSearchParams();
  const authError = searchParams.get('error');
  const authProvider = searchParams.get('provider');
  const returnUrl = searchParams.get('returnUrl');

  // Auth context for password login
  const { loginWithPassword, isLoggingIn, loginError } = useAuthContext();

  /**
   * Handle OAuth callback errors from URL query params
   * @implements FS-001.4.1 - OAuth error handling
   * @implements FS-001.2.2 - OAuth Failure Detection (auto-show password form)
   */
  useEffect(() => {
    if (authError) {
      setError(getErrorMessage(authError, authProvider));

      // Auto-show password form when OAuth provider fails
      // This provides the fallback option as specified in FS-001.2.2
      if (isOAuthProviderError(authError)) {
        setShowPasswordForm(true);
      }
    }
  }, [authError, authProvider]);

  /**
   * Handle login error from auth context
   */
  useEffect(() => {
    if (loginError) {
      setError(loginError.message);
    }
  }, [loginError]);

  /**
   * Handle password login form submission
   * @implements FS-001.2.2 - Password Authentication (Backup)
   * @implements FS-001.5.2 - Return URL handling after login
   */
  const handlePasswordLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await loginWithPassword({ email, password });

      // Redirect to returnUrl if present, otherwise to home
      // This supports the session expiration flow where users are
      // redirected to login with their previous location preserved
      if (returnUrl) {
        const decodedUrl = decodeURIComponent(returnUrl);
        // Safety check: only allow relative URLs to prevent open redirects
        if (decodedUrl.startsWith('/')) {
          navigate(decodedUrl, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      // Provide user-friendly error messages for common login failures
      // @implements FS-001.2.2 - Password Authentication error feedback
      const errorMessage = err instanceof Error ? err.message : 'Login failed';

      // Map common error messages to user-friendly text
      if (
        errorMessage.toLowerCase().includes('invalid') ||
        errorMessage.toLowerCase().includes('credentials') ||
        errorMessage.toLowerCase().includes('unauthorized')
      ) {
        setError('Invalid email or password. Please try again.');
      } else if (errorMessage.toLowerCase().includes('not found')) {
        setError('No account found with this email address.');
      } else if (errorMessage.toLowerCase().includes('password not set')) {
        setError(
          'Password login is not enabled for this account. Please use OAuth.',
        );
      } else {
        setError(errorMessage || 'Login failed. Please try again.');
      }
    }
  };

  /**
   * Toggle password form visibility
   */
  const handleTogglePasswordForm = () => {
    setShowPasswordForm(!showPasswordForm);
    // Clear form state when toggling
    if (showPasswordForm) {
      setEmail('');
      setPassword('');
    }
    // Clear non-OAuth errors when toggling
    if (!authError) {
      setError(null);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Welcome Back
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mb: 4 }}
          >
            Sign in to continue to Jared
          </Typography>

          {/* Error Alert */}
          <Collapse in={!!error}>
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ mb: 3 }}
            >
              {error}
            </Alert>
          </Collapse>

          {/* OAuth Buttons - Primary Login Methods
           * @implements FS-001.2.1 - GitHub and Google OAuth Authentication
           * @implements FS-001.5.2 - Pass returnUrl to OAuth flow for session expiration redirect
           * Hidden when password form is shown to avoid confusion
           */}
          <Collapse in={!showPasswordForm}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <GitHubOAuthButton
                flow="login"
                disabled={isLoggingIn}
                returnUrl={returnUrl || undefined}
              />
              <GoogleOAuthButton
                flow="login"
                disabled={isLoggingIn}
                returnUrl={returnUrl || undefined}
              />
            </Box>
            {/* Provider lock-in helper text
             * @implements EC-001B - Provider Mismatch at Login
             * Helps users understand why login might fail if they use wrong provider
             */}
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mt: 2 }}
            >
              Please sign in with the same provider you used to register.
            </Typography>
          </Collapse>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Divider>

          {/* Toggle Password Login Button */}
          <Button
            variant="text"
            fullWidth
            onClick={handleTogglePasswordForm}
            disabled={isLoggingIn}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {showPasswordForm ? 'Use OAuth instead' : 'Sign in with password'}
          </Button>

          {/* Password Login Form - Hidden when OAuth buttons are shown */}
          <Collapse in={showPasswordForm}>
            <Box component="form" onSubmit={handlePasswordLogin} sx={{ mt: 3 }}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                disabled={isLoggingIn}
                autoComplete="email"
                sx={{ mb: 2 }}
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                disabled={isLoggingIn}
                autoComplete="current-password"
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoggingIn || !email || !password}
                sx={{ textTransform: 'none' }}
              >
                {isLoggingIn ? (
                  <>
                    <CircularProgress
                      size={20}
                      color="inherit"
                      sx={{ mr: 1 }}
                    />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>
          </Collapse>

          {/* Register Link */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Don&apos;t have an account?{' '}
              <Button
                component={RouterLink}
                to="/register"
                color="primary"
                sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
              >
                Register
              </Button>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
